var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useEffect, useRef, useState } from "react";
import { getDiscoverOrganizations } from "@piiqtechnologies/ui-lib";
import { getStoredMemberships, storeMemberships, toStoredMemberships } from "@stores/memberships";
import { getIsAdmin } from "@stores/utils";
/**
 * The starting state for a signed-in identity: staff are ready immediately (they never fetch), and
 * anyone else is ready only if this session already has their memberships stored. Signed out, the
 * bootstrap knows nothing — deliberately `ready: false`, so a stale readiness can't outlive a session.
 */
const seedState = (user) => {
    if (!user)
        return { userId: undefined, ready: false, memberships: null, error: null };
    const stored = getStoredMemberships();
    return {
        userId: user._id,
        ready: getIsAdmin(user) || stored !== null,
        memberships: stored,
        error: null,
    };
};
/**
 * Fetches the caller's organizations once at login and persists them for the session, so
 * `getRedirectPathAfterLogin`/`resolvePostLoginPath` and member pages can branch on a synchronous
 * read afterward.
 *
 * Staff never fetch: they receive every organization implicitly and don't need the membership
 * list, so `ready` flips immediately for them (no fetch-gated delay on the staff entry path).
 * A non-staff user only fetches when nothing is stored yet for this session.
 *
 * Everything the hook holds is scoped to one signed-in identity (`state.userId`). When that
 * identity changes — logout, or a different user signing in — the whole record is re-seeded rather
 * than patched, so no readiness, membership list or error survives into a session it doesn't
 * describe. `fetchedForUserIdRef` is reset with it, so logging back in (as the same person or a
 * different one) fetches again instead of being blocked by the previous session's guard.
 *
 * The re-seed happens *during render*, not in an effect. AuthContext gates its post-login redirect
 * on `ready` and runs that redirect in the same commit as this hook's effects, so a reset deferred
 * to an effect would arrive too late: the redirect would already have fired on the previous
 * session's `ready: true` with session storage empty, stranding the new user on the restricted
 * screen with no later readiness change to correct it.
 *
 * `mountedRef` (flipped only on true unmount, not on every dependency-triggered effect re-run) is
 * what the in-flight fetch checks before writing state — using a per-effect-run `cancelled` closure
 * here instead would be wrong: if the effect re-fires on a new `user` reference while the guarded
 * fetch is still in flight, that closure would mark the *original* fetch as cancelled even though
 * the guard deliberately skips starting a replacement, permanently stalling `ready`.
 *
 * On a rejected fetch (e.g. a transient network error), `ready` still flips true so callers never
 * stall permanently, but nothing is stored and `memberships` stays null — which is how callers tell
 * "we never learned this account's organizations" apart from a stored `[]`, a confirmed zero. See
 * `resolvePostLoginPath`, which routes those two cases to different screens.
 */
export const useMembershipsBootstrap = (user) => {
    const userId = user === null || user === void 0 ? void 0 : user._id;
    const isStaff = getIsAdmin(user);
    const [state, setState] = useState(() => seedState(user));
    const mountedRef = useRef(true);
    // The identity whose fetch has already been started. Doubles as the "is this response still
    // wanted?" check: a fetch whose identity is no longer the one the hook serves must write neither
    // this hook's state nor the session-storage mirror it shares with the rest of the app.
    const fetchedForUserIdRef = useRef(undefined);
    // Re-seed on identity change (React's "adjusting state when a prop changes" pattern). Both writes
    // are confined to this branch, which only runs when the identity genuinely changed and whose
    // `setState` makes the very next render agree — so the render stays deterministic.
    let current = state;
    if (state.userId !== userId) {
        current = seedState(user);
        fetchedForUserIdRef.current = undefined;
        setState(current);
    }
    useEffect(() => () => {
        mountedRef.current = false;
    }, []);
    useEffect(() => {
        if (!userId)
            return;
        if (isStaff) {
            // Normally already seeded ready; this covers a role changing mid-session (syncUserContext)
            // without the identity changing.
            setState((prev) => (prev.userId === userId && !prev.ready ? Object.assign(Object.assign({}, prev), { ready: true }) : prev));
            return;
        }
        // Only the seed above needs to reflect already-stored memberships; this branch intentionally
        // does not re-write state here. Re-syncing from a freshly-`JSON.parse`d (always-new-reference)
        // array on every run would re-render forever.
        if (getStoredMemberships() !== null)
            return;
        if (fetchedForUserIdRef.current === userId)
            return;
        fetchedForUserIdRef.current = userId;
        (() => __awaiter(void 0, void 0, void 0, function* () {
            var _a, _b;
            // The identity that started this fetch is still the one the hook serves. Re-seeding clears
            // the ref, so a logout or user switch mid-flight makes every check below fail.
            const isStillCurrent = () => mountedRef.current && fetchedForUserIdRef.current === userId;
            try {
                const axiosResponse = yield getDiscoverOrganizations();
                const response = axiosResponse;
                const fetchedMemberships = toStoredMemberships((_b = (_a = response === null || response === void 0 ? void 0 : response.data) === null || _a === void 0 ? void 0 : _a.organizations) !== null && _b !== void 0 ? _b : []);
                if (!isStillCurrent())
                    return;
                storeMemberships(fetchedMemberships);
                setState((prev) => prev.userId === userId ? Object.assign(Object.assign({}, prev), { ready: true, memberships: fetchedMemberships, error: null }) : prev);
            }
            catch (caughtError) {
                console.error("useMembershipsBootstrap: failed to fetch discover organizations", caughtError);
                if (!isStillCurrent())
                    return;
                const error = caughtError instanceof Error ? caughtError : new Error(String(caughtError));
                setState((prev) => (prev.userId === userId ? Object.assign(Object.assign({}, prev), { ready: true, error }) : prev));
            }
        }))();
    }, [userId, isStaff]);
    return { ready: current.ready, memberships: current.memberships, error: current.error };
};
